import {
  SandpackCodeEditor,
  SandpackPreview,
  SandpackConsole,
  SandpackFiles,
} from "@codesandbox/sandpack-react";
import { amethyst } from "@codesandbox/sandpack-themes";
import GetFiles from "../mySandpack/GetFiles";
import { CircleNotch } from "@phosphor-icons/react";
import MySandpackProvider from "../mySandpack/MySandpackProvider";
import TripleLayout from "../mySandpack/TripleLayout";
import { useContext } from "react";
import {
  ActivePracticeContext,
  PracticesContext,
} from "../../contexts/contextExporter";

import PracticeTitleContent from "../../components/PracticeTitleContent";
import AiContent from "./AiAnswer";
import { RevealAnswerButton, RevealedAnswerCode } from "./RevealAnswer";
import { IPracticeHistoryItem } from "../../types/generalTypes";
import PracticeHistoryNav from "./PracticeHistoryNav";
import { defaultSpOptions } from "./adapters";
import { IPracticeCode } from "../../types/practiceTypes";

export default function CodePracticePreSandpack({
  currentAiContent,
  setAiReply,
  setUserAnswer,
  history,
  isLive,
}: {
  currentAiContent: string | null;
  setAiReply: React.Dispatch<React.SetStateAction<string | null>>;
  setUserAnswer: React.Dispatch<React.SetStateAction<string | null>>;
  history: IPracticeHistoryItem[];
  isLive: boolean;
}) {
  const {
    practice,
    sendAnswer,
    actionStatus,
    setActionStatus,
    isAnswerRevealed,
  } = useContext(ActivePracticeContext);
  const { nextPractice } = useContext(PracticesContext);

  const codePracticePreSandpack = practice as IPracticeCode;

  const isPastItem = actionStatus.historyIndex > -1;
  const currentHistoryItem = history[actionStatus.historyIndex];

  const files =
    isPastItem &&
      currentHistoryItem?.user_answer &&
      JSON.parse(currentHistoryItem?.user_answer)
      ? JSON.parse(currentHistoryItem?.user_answer)
      : {
        "/index.js": codePracticePreSandpack?.description.preText || "",
      };

  const wantedFiles = "/index.js";

  const tryAgain = () => {
    setActionStatus((prev) => ({ ...prev, ai: "IDLE", historyIndex: -1 }));
    setAiReply(null);
  };

  const onSubmit = (files: SandpackFiles) => {
    const userAnswerObj: Record<string, string> = {};
    wantedFiles.split("|").forEach((element: string) => {
      const f = files[element];
      userAnswerObj[element] =
        typeof f === "string" ? f : (f as { code: string } | undefined)?.code ?? "";
    });

    // Guard: Sandpack editör state'i submit anında bazen henüz senkronlanmamış
    // oluyor ve boş kod gidiyor; AI da "kodunu göremiyorum" gibi kafa karıştıran
    // bir cevap dönüyordu. Boşsa AI'a hiç gönderme, öğrenciyi net uyar (tekrar
    // Gönder'de kod yakalanıyor).
    const isEmpty = wantedFiles
      .split("|")
      .every((element) => !(userAnswerObj[element] || "").trim());
    if (isEmpty) {
      setAiReply(
        "Kodun editörde boş görünüyor. Editöre bir kez tıklayıp kodunu yazdığından emin ol, sonra tekrar Gönder'e bas."
      );
      return;
    }

    setUserAnswer(JSON.stringify(userAnswerObj));
    sendAnswer({ answer: JSON.stringify(userAnswerObj) });
  };

  return (
    <MySandpackProvider
      template="vanilla"
      theme={amethyst}
      className="mySpProvider"
      options={defaultSpOptions.options}
      files={files}
    >
      <TripleLayout
        cx="h-full"
        header={
          <PracticeHistoryNav
            key={practice!.id}
            current={actionStatus.historyIndex}
            total={history.length}
            setCurrent={(newHistoryIndex) =>
              setActionStatus((prev) => ({
                ...prev,
                historyIndex: newHistoryIndex,
              }))
            }
          />
        }
        left={
          <div className="text-base leading-normal h-full overflow-auto relative group">
            <div className="py-6 px-5 mb-2">
              <div className="practiceTitle font-medium p-6 text-white">
                <PracticeTitleContent title={practice!.description.title} />
              </div>

              {isAnswerRevealed && (
                <RevealedAnswerCode code={codePracticePreSandpack?.description?.answer || ""} />
              )}

              <AiContent
                isFocused={isPastItem || actionStatus.ai !== "IDLE"}
                aiReply={currentAiContent}
                setAiReply={setAiReply}
              />
            </div>
            <span className="text-sm text-white absolute bottom-3 right-3 transition-opacity opacity-30 group-hover:opacity-75">
              #{practice!.id}
            </span>
            <SandpackPreview className="h-0 w-0" />
          </div>
        }
        right={[
          <SandpackCodeEditor className="h-full" wrapContent />,
          <SandpackConsole className="h-full" />,
        ]}
        footer={
          <div className="flex gap-2">
            {isPastItem && !actionStatus.canGoNext && !isAnswerRevealed && (
              <button className="practice-button" onClick={tryAgain}>
                Tekrar dene
              </button>
            )}

            {isPastItem && actionStatus.canGoNext && (
              <button
                className="practice-button bg-myGreen/80 border border-myGreen text-black/80 ml-auto hover:bg-myGreen"
                onClick={nextPractice}
              >
                Sonraki Soru
              </button>
            )}

            {!isPastItem && actionStatus.ai === "LOADING" && (
              <button className="practice-button opacity-60 cursor-wait" disabled>
                <CircleNotch size={16} className="animate-spin inline mr-1.5" />
                Değerlendiriliyor...
              </button>
            )}
            {!isPastItem && actionStatus.ai === "IDLE" && !isAnswerRevealed && <GetFiles handleSend={onSubmit} />}

            <RevealAnswerButton tryAgain={tryAgain} isLive={isLive} />
          </div>
        }
      />
    </MySandpackProvider>
  );
}
